import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import api from "../lib/Urls";
import { toast } from "../hooks/use-toast";
import { useChatSocket } from "./ChatSocketContext.jsx";

const NotificationContext = createContext(null);

const parseOtherUserIdFromRoom = (roomId, currentUserId) => {
  if (!roomId || !currentUserId) return null;

  const parts = String(roomId).split("_");
  if (parts.length !== 3) return null;

  const firstUserId = Number(parts[1]);
  const secondUserId = Number(parts[2]);
  const viewerId = Number(currentUserId);

  if (!Number.isFinite(firstUserId) || !Number.isFinite(secondUserId)) {
    return null;
  }

  if (firstUserId === viewerId) return secondUserId;
  if (secondUserId === viewerId) return firstUserId;
  return null;
};

const getNotificationGroupKey = (notification, currentUserId) => {
  if (notification?.type !== "NEW_MESSAGE") {
    return `notification:${notification?.notificationId ?? Math.random()}`;
  }

  if (
    notification?.referenceType === "CHAT_SENDER" &&
    notification?.referenceId
  ) {
    return `chat-sender:${notification.referenceId}`;
  }

  let roomId =
    notification?.referenceType === "CHAT_ROOM"
      ? notification?.referenceId
      : null;

  if (!roomId && notification?.actionUrl) {
    try {
      const query = String(notification.actionUrl).split("?")[1] || "";
      roomId = new URLSearchParams(query).get("roomId");
    } catch {
      roomId = null;
    }
  }

  const otherUserId = parseOtherUserIdFromRoom(roomId, currentUserId);
  if (otherUserId) return `chat-sender:${otherUserId}`;

  return `chat-title:${notification?.title || "unknown"}`;
};

const notificationTime = (notification) => {
  const value = new Date(notification?.createdAt || 0).getTime();
  return Number.isFinite(value) ? value : 0;
};

const mergeIncomingNotification = (current, incoming, currentUserId) => {
  if (!incoming) return current;

  const incomingKey = getNotificationGroupKey(incoming, currentUserId);
  const existingIndex = current.findIndex((notification) => {
    if (
      notification.notificationId === incoming.notificationId
    ) {
      return true;
    }

    return (
      !notification.read &&
      !incoming.read &&
      getNotificationGroupKey(notification, currentUserId) === incomingKey
    );
  });

  let next;

  if (existingIndex >= 0) {
    const existing = current[existingIndex];
    const sameId =
      existing.notificationId === incoming.notificationId;

    const incomingCount = Math.max(
      1,
      Number(incoming.occurrenceCount || 1)
    );
    const existingCount = Math.max(
      1,
      Number(existing.occurrenceCount || 1)
    );

    const merged = {
      ...existing,
      ...incoming,
      occurrenceCount: sameId
        ? Math.max(existingCount, incomingCount)
        : existingCount + incomingCount,
    };

    next = [...current];
    next.splice(existingIndex, 1);
    next.unshift(merged);
  } else {
    next = [
      {
        ...incoming,
        occurrenceCount: Math.max(
          1,
          Number(incoming.occurrenceCount || 1)
        ),
      },
      ...current,
    ];
  }

  return next.sort(
    (a, b) => notificationTime(b) - notificationTime(a)
  );
};

const normalizeNotifications = (list, currentUserId) => {
  return [...list]
    .sort((a, b) => notificationTime(a) - notificationTime(b))
    .reduce(
      (current, notification) =>
        mergeIncomingNotification(
          current,
          notification,
          currentUserId
        ),
      []
    )
    .sort((a, b) => notificationTime(b) - notificationTime(a));
};

export const NotificationProvider = ({ children }) => {
  const {
    token,
    userId,
    isAuthenticated,
    subscribe,
  } = useChatSocket();

  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!token || !userId) {
      setNotifications([]);
      return undefined;
    }

    let cancelled = false;

    api
      .get("/api/notifications/in-app")
      .then((response) => {
        if (!cancelled) {
          setNotifications(
            normalizeNotifications(
              Array.isArray(response.data)
                ? response.data
                : [],
              userId
            )
          );
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error(
            "Failed to load in-app notifications",
            error
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, userId]);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      return undefined;
    }

    return subscribe(
      "/user/queue/notifications",
      (message) => {
        let incoming;

        try {
          incoming = JSON.parse(message.body);
        } catch (error) {
          console.error(
            "Invalid notification payload",
            error
          );
          return;
        }

        setNotifications((current) =>
          mergeIncomingNotification(
            current,
            incoming,
            userId
          )
        );

        toast({
          title:
            incoming.title ||
            "New notification",
          description:
            incoming.message || "",
          duration: 5000,
        });
      }
    );
  }, [isAuthenticated, subscribe, userId]);

  const unreadCount = useMemo(
    () =>
      notifications.reduce((total, notification) => {
        if (notification.read) return total;

        return (
          total +
          Math.max(
            1,
            Number(notification.occurrenceCount || 1)
          )
        );
      }, 0),
    [notifications]
  );

  const reloadNotifications = useCallback(
    async () => {
      if (!token || !userId) {
        return;
      }

      try {
        const response = await api.get(
          "/api/notifications/in-app"
        );

        setNotifications(
          normalizeNotifications(
            Array.isArray(response.data)
              ? response.data
              : [],
            userId
          )
        );
      } catch (error) {
        console.error(
          "Failed to reload notifications",
          error
        );
      }
    },
    [token, userId]
  );

  const markAsRead = useCallback(
    async (notificationId) => {
      if (!notificationId) {
        return;
      }

      setNotifications((current) =>
        current.map((notification) =>
          notification.notificationId ===
          notificationId
            ? {
                ...notification,
                read: true,
              }
            : notification
        )
      );

      try {
        await api.put(
          `/api/notifications/in-app/${notificationId}/read`
        );
      } catch (error) {
        console.error(
          "Failed to mark notification as read",
          error
        );

        await reloadNotifications();
      }
    },
    [reloadNotifications]
  );

  const markAllAsRead = useCallback(
    async () => {
      setNotifications((current) =>
        current.map((notification) => ({
          ...notification,
          read: true,
        }))
      );

      try {
        await api.put(
          "/api/notifications/in-app/read-all"
        );
      } catch (error) {
        console.error(
          "Failed to mark all notifications as read",
          error
        );

        await reloadNotifications();
      }
    },
    [reloadNotifications]
  );

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(
        token && userId
      ),
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      reloadNotifications,
    }),
    [
      token,
      userId,
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      reloadNotifications,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotificationContext must be used inside NotificationProvider"
    );
  }

  return context;
};
